"use client";

import { IProduct } from "@/types/product-d-t";
import ProductItem from "../products/single-product/product-item";
import { useState } from "react";
import { Gift, Sparkles, Package } from "lucide-react";

type IProps = {
  allProducts: IProduct[];
  categoryName?: string;
};

const ShopArea = ({ allProducts, categoryName }: IProps) => {
  const PRODUCTS_PER_LOAD = 12;
  const [visibleCount, setVisibleCount] = useState<number>(PRODUCTS_PER_LOAD);

  const handleSeeMore = () => {
    setVisibleCount((prev) =>
      Math.min(prev + PRODUCTS_PER_LOAD, allProducts.length)
    );
  };

  const productsToShow = allProducts.slice(0, visibleCount);

  return (
    <>
      <section className="shop__area themed-shop-section pt-100 pb-100">
        <div className="container">

          {/* ===== CATEGORY TITLE WITH COUNT ===== */}
          {categoryName && (
            <div className="theme-title-wrapper text-center mb-55">
              {/* FLOATING SPARKLES */}
              <div className="sparkle-wrapper">
                <span className="sparkle-icon s1">🎁</span>
                <span className="sparkle-icon s2">✨</span>
                <span className="sparkle-icon s3">🎀</span>
                <span className="sparkle-icon s4">💝</span>
                <span className="sparkle-icon s5">⭐</span>
                <span className="sparkle-icon s6">🎉</span>
              </div>

              <div className="category-badge">
                <div className="badge-icon-wrapper">
                  <Gift className="badge-icon" />
                </div>
                <span className="badge-text">Gift Collection</span>
              </div>

              <h2 className="theme-section-title">{categoryName}</h2>
              <p className="theme-section-subtitle">
                🎁 Explore {allProducts.length} thoughtfully curated gifts · Perfect for every special occasion
              </p>
              
              {/* DECORATIVE LINE */}
              <div className="decorative-line">
                <span className="line-sparkle">✨</span>
                <span className="line"></span>
                <span className="line-sparkle">✨</span>
              </div>
            </div>
          )}

          {allProducts.length === 0 ? (
            <div className="no-products-wrapper">
              <div className="empty-gift-box">
                <Package style={{ width: "80px", height: "80px", color: "#FF6B6B" }} />
              </div>
              <h3 className="no-products-title">No Gifts Found</h3>
              <p className="no-products-text">Check back soon for more amazing gifts!</p>
            </div>
          ) : (
            <>
              {/* ===== PRODUCTS GRID ===== */}
              <div className="row g-3 product-grid-wrapper">
                {productsToShow.map((product: any) => (
                  <div
                    key={product.id}
                    className="col-xl-3 col-lg-3 col-md-6 col-6 product-col"
                  >
                    <ProductItem product={product} />
                  </div>
                ))}
              </div>

              {/* ===== SEE MORE BUTTON ===== */}
              {visibleCount < allProducts.length && (
                <div className="row mt-45">
                  <div className="col-12 text-center">
                    <button
                      onClick={handleSeeMore}
                      className="theme-see-more-btn"
                    >
                      <Sparkles className="btn-icon" />
                      <span>Discover More Gifts</span>
                      <Gift className="btn-icon" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ================= CSS ================= */}
      <style jsx>{`
        .themed-shop-section {
          background: linear-gradient(135deg, #FFF9F5 0%, #FFFBF8 50%, #FFF5EE 100%);
          position: relative;
          overflow: hidden;
        }

        /* DECORATIVE BACKGROUND CIRCLES */
        .themed-shop-section::before {
          content: "";
          position: absolute;
          top: -100px;
          right: -150px;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          animation: float 8s ease-in-out infinite;
        }

        .themed-shop-section::after {
          content: "";
          position: absolute;
          bottom: -80px;
          left: -120px;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          animation: float 10s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(30px, -30px);
          }
        }

        /* FLOATING SPARKLES */
        .sparkle-wrapper {
          position: relative;
          height: 60px;
          margin-bottom: 20px;
        }

        .sparkle-icon {
          position: absolute;
          font-size: 24px;
          opacity: 0;
          animation: floatSparkle 3.5s ease-in-out infinite;
          left: 50%;
          top: 0;
        }

        .s1 { transform: translateX(-100px); animation-delay: 0s; }
        .s2 { transform: translateX(80px); animation-delay: 0.6s; }
        .s3 { transform: translateX(-150px); animation-delay: 1.2s; }
        .s4 { transform: translateX(130px); animation-delay: 1.8s; }
        .s5 { transform: translateX(-50px); animation-delay: 2.4s; }
        .s6 { transform: translateX(50px); animation-delay: 3s; }

        @keyframes floatSparkle {
          0%, 100% {
            opacity: 0;
            transform: translateX(var(--x, 0)) translateY(0) rotate(0deg) scale(0.8);
          }
          15% { opacity: 0.8; }
          50% {
            opacity: 1;
            transform: translateX(var(--x, 0)) translateY(-40px) rotate(180deg) scale(1.2);
          }
          85% { opacity: 0.8; }
        }

        /* CATEGORY BADGE */
        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 12px 28px;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          color: white;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 28px;
          box-shadow: 0 6px 24px rgba(255,107,107,0.35);
          animation: badgePulse 3s ease-in-out infinite;
          border: 3px solid rgba(255,255,255,0.5);
        }

        .badge-icon-wrapper {
          width: 32px;
          height: 32px;
          background: rgba(255,255,255,0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: rotate 4s linear infinite;
        }

        .badge-icon {
          width: 18px;
          height: 18px;
        }

        @keyframes badgePulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 6px 24px rgba(255,107,107,0.35);
          }
          50% {
            transform: scale(1.06);
            box-shadow: 0 8px 32px rgba(255,107,107,0.45);
          }
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .theme-title-wrapper {
          margin-bottom: 55px;
          text-align: center;
        }

        .theme-section-title {
          font-size: 52px;
          font-weight: 900;
          text-transform: capitalize;
          letter-spacing: -1px;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 50%, #FF6B6B 100%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientFlow 5s ease infinite, fadeUp 0.7s ease;
          filter: drop-shadow(0 2px 8px rgba(255,107,107,0.2));
          line-height: 1.1;
        }

        .theme-section-subtitle {
          font-size: 18px;
          color: #666;
          letter-spacing: 0.3px;
          max-width: 700px;
          margin: 0 auto 20px;
          font-weight: 600;
          line-height: 1.7;
        }

        /* DECORATIVE LINE */
        .decorative-line {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-top: 24px;
        }

        .line {
          width: 120px;
          height: 3px;
          background: linear-gradient(90deg, transparent, #FF6B6B, transparent);
          border-radius: 10px;
        }

        .line-sparkle {
          font-size: 18px;
          animation: sparkleRotate 2s ease-in-out infinite;
        }

        @keyframes sparkleRotate {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(180deg); }
        }

        @keyframes gradientFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(25px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* NO PRODUCTS STATE */
        .no-products-wrapper {
          text-align: center;
          padding: 100px 20px;
          background: #fff;
          border-radius: 24px;
          border: 2px solid #FFF0E6;
          box-shadow: 0 8px 24px rgba(255,140,83,0.1);
        }

        .empty-gift-box {
          width: 140px;
          height: 140px;
          margin: 0 auto 24px;
          background: linear-gradient(135deg, #FFF9F5 0%, #FFE9E0 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: bounce 2s ease-in-out infinite;
          border: 3px solid #FFF0E6;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        .no-products-title {
          font-size: 28px;
          font-weight: 900;
          color: #2C2C2C;
          margin-bottom: 12px;
        }

        .no-products-text {
          font-size: 16px;
          color: #999;
          font-weight: 600;
        }

        /* PRODUCT GRID */
        .product-grid-wrapper {
          margin: 0 auto;
          max-width: 100%;
        }

        .product-col {
          display: flex;
          justify-content: center;
        }

        /* SEE MORE BUTTON */
        .theme-see-more-btn {
          background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
          color: #fff;
          border: 3px solid rgba(255,255,255,0.5);
          padding: 18px 55px;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 1px;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 6px 20px rgba(255,107,107,0.35),
            0 10px 35px rgba(255,107,107,0.2);
          margin-top: 50px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          position: relative;
          overflow: hidden;
        }

        .theme-see-more-btn::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255,255,255,0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }

        .theme-see-more-btn:hover::before {
          width: 400px;
          height: 400px;
        }

        .theme-see-more-btn:hover {
          transform: translateY(-4px) scale(1.04);
          box-shadow: 
            0 10px 28px rgba(255,107,107,0.45),
            0 16px 45px rgba(255,107,107,0.3);
        }

        .btn-icon {
          width: 20px;
          height: 20px;
          position: relative;
          z-index: 1;
        }

        .theme-see-more-btn span {
          position: relative;
          z-index: 1;
        }

        /* MOBILE RESPONSIVE */
        @media (max-width: 767px) {
          .themed-shop-section {
            padding-left: 0;
            padding-right: 0;
          }

          .container {
            padding-left: 15px;
            padding-right: 15px;
            max-width: 100%;
          }

          .product-grid-wrapper {
            margin-left: 0;
            margin-right: 0;
            padding: 0;
          }

          .product-col {
            padding-left: 6px;
            padding-right: 6px;
          }

          .sparkle-wrapper {
            height: 45px;
            margin-bottom: 15px;
          }

          .sparkle-icon {
            font-size: 18px;
          }

          .category-badge {
            font-size: 12px;
            padding: 10px 22px;
            margin-bottom: 22px;
            gap: 10px;
          }

          .badge-icon-wrapper {
            width: 28px;
            height: 28px;
          }

          .badge-icon {
            width: 16px;
            height: 16px;
          }

          .theme-section-title {
            font-size: 32px;
            margin-bottom: 14px;
            letter-spacing: -0.5px;
          }

          .theme-section-subtitle {
            font-size: 15px;
            max-width: 95%;
            padding: 0 5px;
          }

          .decorative-line {
            margin-top: 18px;
            gap: 12px;
          }

          .line {
            width: 80px;
            height: 2px;
          }

          .line-sparkle {
            font-size: 16px;
          }

          .theme-see-more-btn {
            width: auto;
            padding: 15px 42px;
            font-size: 14px;
            margin-top: 35px;
            gap: 10px;
          }

          .btn-icon {
            width: 18px;
            height: 18px;
          }

          .empty-gift-box {
            width: 100px;
            height: 100px;
            margin-bottom: 20px;
          }

          .empty-gift-box :global(svg) {
            width: 60px !important;
            height: 60px !important;
          }

          .no-products-title {
            font-size: 22px;
          }

          .no-products-text {
            font-size: 14px;
          }

          .no-products-wrapper {
            padding: 60px 20px;
          }

          .themed-shop-section::before,
          .themed-shop-section::after {
            width: 250px;
            height: 250px;
          }
        }

        @media (max-width: 480px) {
          .theme-section-title {
            font-size: 28px;
          }

          .theme-section-subtitle {
            font-size: 14px;
          }

          .theme-see-more-btn {
            padding: 13px 36px;
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

export default ShopArea;